import { useState } from "react";
import {
  usePendingRequisitionReturns,
  useApproveRequisitionReturn,
  useRejectRequisitionReturn,
} from "../hooks/useRequisition";
import {
  usePendingTrayReturns,
  useApproveTrayReturn,
  useRejectTrayReturn,
} from "@/features/dispatch/hooks/useDispatch";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotification } from "@/shared/hooks/useNotification";
import { useApiError } from "@/shared/hooks/useApiError";
import type { RequisitionItemReturn } from "../services/requisitionApi";
import type { TrayReturnDto } from "@/features/dispatch/services/dispatchApi";

type Decision = { kind: "approve"; row: RequisitionItemReturn }
  | { kind: "reject"; row: RequisitionItemReturn };

type TrayDecision = { kind: "approve"; row: TrayReturnDto }
  | { kind: "reject"; row: TrayReturnDto };

export const ReturnApprovalsPage = () => {
  const { data: returns = [], isLoading, error } = usePendingRequisitionReturns();
  const approve = useApproveRequisitionReturn();
  const reject = useRejectRequisitionReturn();

  const { data: trayReturns = [], isLoading: trayLoading } = usePendingTrayReturns();
  const approveTray = useApproveTrayReturn();
  const rejectTray = useRejectTrayReturn();

  const notify = useNotification();
  const handleApiError = useApiError();

  const [decision, setDecision] = useState<Decision | null>(null);
  const [trayDecision, setTrayDecision] = useState<TrayDecision | null>(null);
  const [notesInput, setNotesInput] = useState("");

  if (isLoading || trayLoading) return <LoadingAnimation fullScreen />;
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load pending returns</p>
      </div>
    );
  }

  const startDecision = (kind: Decision["kind"], row: RequisitionItemReturn) => {
    setDecision({ kind, row } as Decision);
    setNotesInput("");
  };

  const submitDecision = () => {
    if (!decision) return;
    if (decision.kind === "approve") {
      approve.mutate(
        { returnId: decision.row.id, approvalNotes: notesInput || undefined },
        {
          onSuccess: () => {
            notify.success(`Return approved — stock / seed batch updated.`);
            setDecision(null);
          },
          onError: (e) => handleApiError(e),
        }
      );
    } else {
      if (!notesInput.trim()) {
        notify.warning("Reason is required when rejecting.");
        return;
      }
      reject.mutate(
        { returnId: decision.row.id, rejectionReason: notesInput.trim() },
        {
          onSuccess: () => {
            notify.success("Return rejected.");
            setDecision(null);
          },
          onError: (e) => handleApiError(e),
        }
      );
    }
  };

  const pending = approve.isPending || reject.isPending || approveTray.isPending || rejectTray.isPending;

  const startTrayDecision = (kind: TrayDecision["kind"], row: TrayReturnDto) => {
    setTrayDecision({ kind, row } as TrayDecision);
    setNotesInput("");
  };

  const submitTrayDecision = () => {
    if (!trayDecision) return;
    if (trayDecision.kind === "approve") {
      approveTray.mutate(
        { id: trayDecision.row.id, approvalNotes: notesInput || undefined },
        {
          onSuccess: () => {
            notify.success("Tray return approved — trays credited to store.");
            setTrayDecision(null);
          },
          onError: (e) => handleApiError(e),
        }
      );
    } else {
      if (!notesInput.trim()) {
        notify.warning("Reason is required when rejecting.");
        return;
      }
      rejectTray.mutate(
        { id: trayDecision.row.id, rejectionReason: notesInput.trim() },
        {
          onSuccess: () => {
            notify.success("Tray return rejected.");
            setTrayDecision(null);
          },
          onError: (e) => handleApiError(e),
        }
      );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Return Approvals</h1>
        <p className="text-gray-500 mt-1">
          Review unused-item returns submitted from sowing. Approving posts a
          stock return movement, or credits the customer's seed batch when
          applicable.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="requisitions.approve_return" />
        </div>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Sowing</TableHead>
              <TableHead className="text-right">Issued</TableHead>
              <TableHead className="text-right">Used</TableHead>
              <TableHead className="text-right">Returning</TableHead>
              <TableHead>Submitted by</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {returns.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No pending returns.
                </TableCell>
              </TableRow>
            )}
            {returns.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <div className="font-medium">{r.inputName}</div>
                  <div className="text-xs text-muted-foreground">
                    Requisition {r.requisitionNumber}
                  </div>
                </TableCell>
                <TableCell>
                  {r.isCustomerSeedReturn ? (
                    <Badge variant="outline">
                      🌱 Customer batch · {r.seedBatchNumber}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Company stock</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {r.sowingInstructionNumber ?? "—"}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {r.quantityIssuedOnItem} {r.unitOfMeasure}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {r.quantityUsed} {r.unitOfMeasure}
                </TableCell>
                <TableCell className="text-right text-sm font-semibold">
                  {r.quantity} {r.unitOfMeasure}
                </TableCell>
                <TableCell className="text-sm">
                  <div>{r.submittedByUserName}</div>
                  <div className="text-xs text-muted-foreground">
                    Submitted {new Date(r.submittedAt).toLocaleString()}
                  </div>
                  {r.returnedDate && (
                    <div className="text-xs text-muted-foreground">
                      Returned {new Date(r.returnedDate).toLocaleDateString()}
                    </div>
                  )}
                  {r.submissionNotes && (
                    <div className="text-xs text-muted-foreground mt-1">
                      "{r.submissionNotes}"
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    <Button
                      size="sm"
                      onClick={() => startDecision("approve", r)}
                      disabled={pending}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startDecision("reject", r)}
                      disabled={pending}
                    >
                      Reject
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decision?.kind === "approve" ? "Approve return" : "Reject return"}
            </DialogTitle>
            <DialogDescription>
              {decision?.kind === "approve"
                ? "Posts the stock movement / credits the seed batch and updates the requisition's returned total."
                : "Rejection is final — the sowing manager will need to submit a new return if needed."}
            </DialogDescription>
          </DialogHeader>

          {decision && (
            <div className="space-y-3 text-sm">
              <div className="rounded border p-3 bg-muted/30 space-y-1">
                <div>
                  <span className="text-muted-foreground">Item:</span>{" "}
                  <span className="font-medium">{decision.row.inputName}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Requisition:</span>{" "}
                  {decision.row.requisitionNumber}
                </div>
                <div>
                  <span className="text-muted-foreground">Sowing usage:</span>{" "}
                  {decision.row.quantityUsed} {decision.row.unitOfMeasure} used
                  · returning {decision.row.quantity}{" "}
                  {decision.row.unitOfMeasure}
                </div>
                {decision.row.isCustomerSeedReturn && (
                  <div className="text-emerald-700 text-xs">
                    🌱 Customer seed batch — return credits{" "}
                    {decision.row.seedBatchNumber}
                  </div>
                )}
              </div>

              <Field>
                <FieldLabel htmlFor="decisionNotes">
                  {decision.kind === "approve"
                    ? "Approval notes (optional)"
                    : "Rejection reason"}{" "}
                  {decision.kind === "reject" && (
                    <span className="text-red-500">*</span>
                  )}
                </FieldLabel>
                <Textarea
                  id="decisionNotes"
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  placeholder={
                    decision.kind === "approve"
                      ? "Anything to record"
                      : "Why is this return being rejected?"
                  }
                  disabled={pending}
                />
              </Field>

              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setDecision(null)}
                  disabled={pending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitDecision}
                  disabled={pending}
                  variant={
                    decision.kind === "reject" ? "destructive" : "default"
                  }
                >
                  {pending ? "Submitting..." : decision.kind === "approve" ? "Approve" : "Reject"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tray Returns Section */}
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Dispatch Tray Returns</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Trays returned from customer deliveries. Approving credits trays back
          to store inventory.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="dispatch.approve_tray_return" />
        </div>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dispatch</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Trays Returned</TableHead>
              <TableHead>Recorded by</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trayReturns.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No pending tray returns.
                </TableCell>
              </TableRow>
            )}
            {trayReturns.map((tr) => (
              <TableRow key={tr.id}>
                <TableCell>
                  <div className="font-medium">{tr.dispatchNumber}</div>
                  <div className="text-xs text-muted-foreground">
                    Order {tr.orderNumber}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{tr.customerName}</TableCell>
                <TableCell className="text-right font-semibold">
                  {tr.quantityReturned} trays
                </TableCell>
                <TableCell className="text-sm">
                  {tr.recordedByUserName}
                </TableCell>
                <TableCell className="text-sm">
                  {new Date(tr.returnDate).toLocaleDateString()}
                  {tr.notes && (
                    <div className="text-xs text-muted-foreground mt-1">
                      "{tr.notes}"
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    <Button
                      size="sm"
                      onClick={() => startTrayDecision("approve", tr)}
                      disabled={pending}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startTrayDecision("reject", tr)}
                      disabled={pending}
                    >
                      Reject
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Tray Return Decision Dialog */}
      <Dialog open={!!trayDecision} onOpenChange={(o) => !o && setTrayDecision(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {trayDecision?.kind === "approve" ? "Approve tray return" : "Reject tray return"}
            </DialogTitle>
            <DialogDescription>
              {trayDecision?.kind === "approve"
                ? "Approving will credit the returned trays back to store inventory."
                : "Rejecting will reverse the return and restore the dispatch fulfillment."}
            </DialogDescription>
          </DialogHeader>

          {trayDecision && (
            <div className="space-y-3 text-sm">
              <div className="rounded border p-3 bg-muted/30 space-y-1">
                <div>
                  <span className="text-muted-foreground">Dispatch:</span>{" "}
                  <span className="font-medium">{trayDecision.row.dispatchNumber}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Customer:</span>{" "}
                  {trayDecision.row.customerName}
                </div>
                <div>
                  <span className="text-muted-foreground">Trays:</span>{" "}
                  {trayDecision.row.quantityReturned} returned
                </div>
              </div>

              <Field>
                <FieldLabel htmlFor="trayDecisionNotes">
                  {trayDecision.kind === "approve"
                    ? "Approval notes (optional)"
                    : "Rejection reason"}{" "}
                  {trayDecision.kind === "reject" && (
                    <span className="text-red-500">*</span>
                  )}
                </FieldLabel>
                <Textarea
                  id="trayDecisionNotes"
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  placeholder={
                    trayDecision.kind === "approve"
                      ? "Anything to record"
                      : "Why is this tray return being rejected?"
                  }
                  disabled={pending}
                />
              </Field>

              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setTrayDecision(null)}
                  disabled={pending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitTrayDecision}
                  disabled={pending}
                  variant={
                    trayDecision.kind === "reject" ? "destructive" : "default"
                  }
                >
                  {pending ? "Submitting..." : trayDecision.kind === "approve" ? "Approve" : "Reject"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
